import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
  conversationUuidOf,
  isConversationUrl,
} from '../conversation/conversation-address.helper';
// Concrete file, not the shared barrel: the barrel reaches this store module
// back through its selectors (an import cycle).
import { DocumentViewerActions } from '../shared/actions/document-viewer.actions';
import { ChatActions } from './chats.actions';
import { chatsFeature } from './chats.reducer';

/**
 * The one effect that turns the address into the selection (SB-3944 / KD-29).
 * The URL is the only input: the activate guard has already decided the
 * address, so this effect never redirects.
 */
@Injectable()
export class ChatsRouterEffects {
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  /**
   * - `NavigationStart`, leaving the conversation route: closes the
   *   conversation's side panels. Both are rendered by the /chats *shell*, not
   *   by the conversation route, so neither is torn down by navigation: the
   *   assistant profile is driven by `isAssistantProfileOpened` in the chats
   *   slice, the document viewer by `viewMode` in the layout slice.
   *   `closeDocumentViewer` resets both halves of the viewer's state. At
   *   `NavigationStart`, `router.url` is still the URL being left, which keeps
   *   the timing of the guard this replaced (before the view transition).
   * - `NavigationEnd` on a conversation URL: selects that chat when it is not
   *   already selected. A query-only change keeps the uuid, so it selects
   *   nothing (the mobile `?navigationOpen` toggle, a re-key that already
   *   moved the selection).
   *
   * The selection is never cleared on leaving the conversation.
   */
  readonly routerNavigation$ = createEffect(() =>
    this.router.events.pipe(
      filter(
        (event): event is NavigationStart | NavigationEnd =>
          event instanceof NavigationStart || event instanceof NavigationEnd,
      ),
      withLatestFrom(this.store.select(chatsFeature.selectSelectedChatUuid)),
      mergeMap(([event, selectedUuid]) => {
        if (event instanceof NavigationStart) {
          return isConversationUrl(this.router.url) &&
            !isConversationUrl(event.url)
            ? [
                ChatActions.closeAssistantProfile(),
                DocumentViewerActions.closeDocumentViewer(),
              ]
            : [];
        }
        const uuid = conversationUuidOf(event.urlAfterRedirects);
        return uuid && uuid !== selectedUuid
          ? [ChatActions.selectChat({ uuid })]
          : [];
      }),
    ),
  );
}
